<template>
	<div class="buybox buybox-selector" ref="buyboxContainer" :class="{ 'v-3': buyRentExpVariant === 'variant_3' }">
		<!-- for both trial and non trial -->
		<PromotionPlacement
			v-if="showTopPositionPromotionPlacement"
			class="offer-container"
			:showFreeTrial="showFreeTrial"
			:isFreeBuyBox="filter === 'free'"
			:additionalContexts="additionalContexts"
			:trackingContexts="qualityTvTrackingContexts"
			:title="title"
			:offers="allOffers"
			:seasonNumber="seasonNumber"
			:episodeNumber="episodeNumber"
		/>
		<!-- for both trial and non trial -->

		<!-- THIS IS a Temporary Fix logic of showing the BuyBoxPromotionRowNew needs to be refactored -->
		<NoOffers
			v-if="showTopPositionNoOffers"
			class="offer-container"
			:title="title"
			:isTitleInWatchlist="isInWatchlist"
			:filter="filter"
			:additionalContexts="additionalContexts"
			@onNotifyMe="$emit('onNotifyMe')"
		/>

		<template v-for="(offer, index) in visibleOffers">
			<ListOffer
				v-if="offer.rowType !== 'freeTrial'"
				:key="`${offer?.id}_${offer?.package?.packageId}_${index}`"
				class="offer-container"
				:offer="offer"
				:isBundle="!!offer.bundleId"
				:title="title"
				:additionalContexts="additionalContexts"
				:isEpisode="isEpisodeBuybox"
				:seasonNumber="seasonNumber"
				:episodeNumber="episodeNumber"
				:rowIndex="index"
			/>

			<PromotionPlacement
				v-else
				:key="`promotion_${offer?.id}_${offer?.package.packageId}_${index}`"
				class="offer-container"
				:showFreeTrial="showFreeTrial"
				:isFreeBuyBox="filter === 'free'"
				:additionalContexts="additionalContexts"
				:trackingContexts="qualityTvTrackingContexts"
				:title="title"
				:offers="allOffers"
				:seasonNumber="seasonNumber"
				:episodeNumber="episodeNumber"
				:rowIndex="index"
			/>
		</template>

		<template v-if="upcomingReleases.length > 0">
			<ListUpcoming
				v-for="(release, index) in upcomingReleases"
				:key="`${release.offer.package?.id}_${release.offer?.package?.packageId}_${index}`"
				:upcomingRelease="release"
				:title="title"
				:filter="filter"
				:isTitleInWatchlist="isInWatchlist"
				:additionalContexts="additionalContexts"
				@onNotifyMe="$emit('onNotifyMe')"
			/>
		</template>

		<NotifyMe
			v-if="showNotifyMeNew"
			class="offer-container"
			:title="title"
			:isTitleInWatchlist="isInWatchlist"
			:filter="filter"
			:additionalContexts="additionalContexts"
			@onNotifyMe="$emit('onNotifyMe')"
		/>
		<PromotionPlacement
			v-if="filter === 'all' && showNoOffers"
			class="offer-container"
			:showFreeTrial="showFreeTrial"
			:isFreeBuyBox="false"
			:additionalContexts="additionalContexts"
			:trackingContexts="qualityTvTrackingContexts"
			:title="title"
			:offers="allOffers"
			:seasonNumber="seasonNumber"
			:episodeNumber="episodeNumber"
		/>
		<NotifyMe
			v-if="filter !== 'free' && hasOnlyUpcomingOffers"
			class="offer-container"
			:title="title"
			:isTitleInWatchlist="isInWatchlist"
			:filter="filter"
			:additionalContexts="additionalContexts"
			@onNotifyMe="$emit('onNotifyMe')"
		/>

		<!-- Hidden offers (cinema/physical) rendered at end for SEO, will be hidden via CSS -->
		<template v-for="(offer, index) in hiddenOffers">
			<ListOffer
				v-if="offer.rowType !== 'freeTrial'"
				:key="`hidden_${offer?.id}_${offer?.package?.packageId}_${index}`"
				class="offer-container--other"
				:offer="offer"
				:isBundle="!!offer.bundleId"
				:title="title"
				:additionalContexts="additionalContexts"
				:isEpisode="isEpisodeBuybox"
				:seasonNumber="seasonNumber"
				:episodeNumber="episodeNumber"
			/>
		</template>
	</div>
</template>

<script setup lang="ts">
import { computed, provide } from 'vue';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import { OfferLocation } from '@/helpers/offer-helper';
import { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';

import { allowedPromotionLanguages, usePromotion } from '@/helpers/composables/usePromotion';
import type { BuyboxRegularOffer, UpcomingReleaseBuybox } from '@/features/buybox/types/types.ts';

import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { useOverProviderExp } from '@/components/experiments/OverProviderExp';
import { qtvProvidersByType } from '@/features/buybox/helpers/quality-tv-data';
import { useExperiment } from '@/libraries/experiment';
import { useJWTVCampaign } from '@/features/jwtv-titles-rental/composables/useJWTVCampaign';

import NoOffers from '@/features/buybox/components/list/NoOffers.vue';
import NotifyMe from '@/features/buybox/components/list/NotifyMe.vue';
import ListUpcoming from '@/features/buybox/components/list/ListUpcoming.vue';
import ListOffer from '@/features/buybox/components/list/ListOffer.vue';

const PromotionPlacement = defineAsyncComponent(
	() => import('@/features/buybox/components/list/PromotionPlacement.vue')
);

const props = defineProps<{
	regularOffers: BuyboxRegularOffer[];
	allOffers: TitleOfferFragment[];
	filter: BuyBoxFilterOptions;
	title: TitleDetail;
	additionalContexts: SnowplowContext[];
	upcomingReleases: UpcomingReleaseBuybox[];
	isInWatchlist: boolean;
	seasonNumber?: number | null;
	episodeNumber?: number | null;
	isEpisodeBuybox?: boolean;
	hasVODOffers: boolean;
}>();

defineEmits<{ (e: 'onNotifyMe'): void }>();

const { language, country } = storeToRefs(useLanguageStore());
const { isLoggedIn } = storeToRefs(useUserStore());

provide('offerLocation', OfferLocation.REGULAR_BUYBOX);

const { promotionOffer, inPromotionCatalogue, catalogueItem } = usePromotion({
	title: () => props.title,
	offers: () => props.allOffers,
});

const { activeVariantWithControlGroup: buyRentExpVariant } = useExperiment('EXP_MERGE_BUY_RENT');

const showFreeTrial = computed(
	() =>
		!!promotionOffer.value &&
		[BuyBoxFilterOptions.ALL, BuyBoxFilterOptions.FREE, BuyBoxFilterOptions.SUBSCRIPTION].includes(props.filter) &&
		// splitting language for locale specific ones like Spanish - Spain, Spanish - Latin America, etc
		allowedPromotionLanguages.includes(language.value.split('-')[0])
);

const hasFreeTrialOffers = computed(() => props?.regularOffers.length > 0 || inPromotionCatalogue.value);

const showNoOffers = computed(() => props.regularOffers.length === 0);

const showNotifyMeNew = computed(() => {
	return props.filter === BuyBoxFilterOptions.FREE ? hasFreeTrialOffers.value : !showNoOffers.value;
});

const hasOnlyUpcomingOffers = computed(() => props.upcomingReleases.length > 0 && showNoOffers.value);

//  Quality TV
const qualityTvTrackingContexts = computed(() => {
	const titleObjectId = props.title.__typename === 'Season' ? props.title.show.objectId : props.title.objectId;
	const titleObjectType = props.title.__typename === 'Season' ? props.title.show.objectType : props.title.objectType;
	const titleContext = new SnowplowTitleContextGraphql(
		titleObjectId,
		titleObjectType,
		props.seasonNumber ?? null,
		props.episodeNumber ?? null
	);

	return (props.additionalContexts || []).concat([titleContext]);
});
// Quality TV

const isFreeTrialPositionFeature = computed(() => ['GB', 'CA'].includes(country.value) && !isLoggedIn.value);

const { isOverProviderTitle } = useOverProviderExp({ promotionOffer });

const includeTVODQTVonRentOrBuy = computed(() => {
	// Include TVOD QTV (which acts as a 'freeTrial' offer) also on Rent and Buy filters. Other free trial offers are not included.
	return (
		catalogueItem.value?.placement === 'qtv' &&
		qtvProvidersByType['TVOD'].includes(catalogueItem.value.shortName) &&
		[BuyBoxFilterOptions.BUY, BuyBoxFilterOptions.RENT].includes(props.filter)
	);
});

const { hasActiveJwtvCampaign } = useJWTVCampaign();

const _allOffers = computed<BuyboxRegularOffer[]>(() => {
	let offers: BuyboxRegularOffer[] = [...props.regularOffers];
	// Add promotion offer to the list of offers
	if (
		!showNoOffers.value &&
		([BuyBoxFilterOptions.ALL, BuyBoxFilterOptions.SUBSCRIPTION, BuyBoxFilterOptions.FREE].includes(props.filter) ||
			includeTVODQTVonRentOrBuy.value) &&
		!!promotionOffer.value
	) {
		// OVER_PROVIDER
		if (isOverProviderTitle.value) {
			offers = [
				{ ...promotionOffer.value, rowType: 'freeTrial' } as BuyboxRegularOffer,
				// and we filter out the offers that are of the same provider as the free trial
				...offers.filter(offer => offer.package.shortName !== promotionOffer.value?.package.shortName),
			];
		} else {
			const rowPosition = isFreeTrialPositionFeature.value ? 3 : 2;
			const freeTrialRow = { ...promotionOffer.value, rowType: 'freeTrial' } as BuyboxRegularOffer;
			offers.splice(Math.min(rowPosition, offers.length), 0, freeTrialRow);
		}
	}

	if (hasActiveJwtvCampaign.value) {
		// if the title has a JWTV offer, only show the JWTV offer, otherwise show no offers (JWTV is the only offer that should be shown during the campaign)
		const hasJWTVOffer = props.allOffers.some(offer => offer.package.shortName === 'jwt');
		if (hasJWTVOffer) {
			const jwtvOffer = offers.find(offer => offer.package.shortName === 'jwt');
			if (jwtvOffer) {
				offers = [jwtvOffer];
			} else {
				offers = [];
			}
		}
	}

	return offers.filter(Boolean);
});

/**
 * ALL: Promotions shown after NoOffers component instead (line 89)
 * PHYSICAL: Physical shouldn't show streaming promotions
 * CINEMA: Theater shouldn't show streaming promotions
 */
const showTopPositionPromotionPlacement = computed(
	() =>
		showNoOffers.value &&
		props.filter !== BuyBoxFilterOptions.ALL &&
		props.filter !== BuyBoxFilterOptions.PHYSICAL &&
		props.filter !== BuyBoxFilterOptions.CINEMA
);

/**
 * Filter exclusions:
 * PHYSICAL: Disc tab shouldn't render anything unless there are actual physcial media offers
 * CINEMA: same as above
 *
 * Special handling:
 * FREE filter: Only show if not in promotion catalogue (promotions show instead)
 * Other filters: Only show if no upcoming releases (upcoming releases show instead)
 */
const showTopPositionNoOffers = computed(
	() =>
		props.filter !== BuyBoxFilterOptions.PHYSICAL &&
		props.filter !== BuyBoxFilterOptions.CINEMA &&
		(props.filter === BuyBoxFilterOptions.FREE
			? showNoOffers.value && !inPromotionCatalogue.value
			: showNoOffers.value && props.upcomingReleases.length === 0)
);

// Split offers into visible and hidden for correct ordering
const visibleOffers = computed(() => {
	if (props.filter !== BuyBoxFilterOptions.ALL || !props.hasVODOffers) {
		return _allOffers.value;
	}
	// Filter out cinema and physical offers
	return _allOffers.value.filter(offer => {
		if (offer.monetizationType === 'CINEMA') return false;
		if (offer.presentationType && ['DVD', 'BLURAY', 'BLURAY_4K'].includes(offer.presentationType)) return false;
		return true;
	});
});

const hiddenOffers = computed(() => {
	if (props.filter !== BuyBoxFilterOptions.ALL || !props.hasVODOffers) {
		return [];
	}
	// Only cinema and physical offers
	return _allOffers.value.filter(offer => {
		if (offer.monetizationType === 'CINEMA') return true;
		if (offer.presentationType && ['DVD', 'BLURAY', 'BLURAY_4K'].includes(offer.presentationType)) return true;
		return false;
	});
});
</script>

<style lang="scss" scoped>
.buybox {
	border-radius: 8px;

	.offer-container:nth-child(2n) {
		background: #111924;

		:deep(.pp-exp-qtv) {
			--fade-color: #111924;
		}
	}

	.offer-container:nth-child(2n-1) {
		background: #0c131e;

		:deep(.pp-exp-qtv) {
			--fade-color: #0c131e;
		}
	}

	.offer-container--other {
		display: none;
	}
}

// EXP MERGE BUY RENT
.v-3 {
	&.buybox {
		@media (max-width: 1050px) {
			margin-left: -16px;
			margin-right: -16px;
		}

		@media (min-width: 1050px) {
			border-radius: 8px;
			overflow: hidden;
		}
	}
}
// EXP MERGE BUY RENT
</style>
